import React from "react";
import { keyframes } from "@emotion/react";

import { Box } from "@UI/Components/Box/Box";
import { HStack } from "@UI/Components/Stack/Stack";
import { Text } from "@UI/Components/Text/Text";
import { Button } from "@UI/Components/Button/Button";
import {
  ICandidate,
  ICandidateCardProps,
  ICandidateOfferModalProps,
} from "../../types";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "../messages";
import { Icon } from "@UI/Components/Icon/Icon";
import { Xmark } from "@UI/Components/Icons/Xmark";
import { Comment } from "@UI/Components/Icons/Comment";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";

/** Soft ring expanding and fading behind the unread badge; off under reduced motion. */
const badgePing = keyframes`
  0% { transform: scale(1); opacity: 0.6; }
  75%, 100% { transform: scale(1.6); opacity: 0; }
`;

const UNREAD_BADGE_SX = {
  isolation: "isolate",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    zIndex: -1,
    borderRadius: "inherit",
    background: "inherit",
    animation: `${badgePing} 2s ease-out infinite`,
  },
  "@media (prefers-reduced-motion: reduce)": {
    "&::before": { animation: "none" },
  },
};

export const CandidateActions = (
  props: {
    conversationId: number;
    unreadMessagesCount?: number;
    hasServiceOffer: boolean;
    isLoading: boolean;
    serviceOfferId?: number;
  } & Pick<ICandidate, "offerWithdrawn" | "offerAsked"> &
    ICandidateCardProps &
    Pick<ICandidateOfferModalProps, "onDeclineOfferModalOpen">
) => {
  const {
    unreadMessagesCount = 0,
    offerAsked,
    offerWithdrawn,
    hasServiceOffer,
    isLoading,
    onDeclineOfferModalOpen,
    serviceOfferId,
  } = props;

  const { currentUser } = useCurrentUser();
  const isSubscribed = currentUser?.subscribed ?? false;

  return (
    <HStack spacing="8px">
      {!hasServiceOffer ? (
        <Button
          flex="1"
          variant="outline"
          size="lg"
          borderColor="primary"
          color="primary"
          _disabled={{
            color: "gray.300",
            borderColor: "gray.300",
            cursor: "auto",
          }}
          _hover={{
            borderColor: "seaGreen",
            background: "seaGreen",
            color: "white",
          }}
          isDisabled={
            isLoading || offerWithdrawn || (offerAsked && !hasServiceOffer)
          }
          data-bs-toggle="offcanvas"
          data-bs-target={`#offcanvas-conversation-${props.conversationId}`}
          aria-controls="offcanvasRight"
          onClick={() => {
            let iframe = document.querySelector(`iframe[src*='/conversations/${props.conversationId}']`) as HTMLIFrameElement | null;

            if (!iframe) {
              iframe = document.querySelector(`iframe[data-offcanvas-src*='/conversations/${props.conversationId}']`) as HTMLIFrameElement | null;
            }

            if (iframe) {
              const clickButton = () => {
                const button = document.querySelector(
                  `[data-conversation-id="${props.conversationId}"][data-ask-an-offer-action="ask-an-offer"]`
                );
                if (button instanceof HTMLElement) {
                  button.click();
                }
              };

              const hasRealSrc = iframe.src && iframe.src !== '' && !iframe.src.includes('about:blank');
              const isLoaded = iframe.contentWindow && iframe.contentWindow.document.readyState === 'complete';

              if (hasRealSrc && isLoaded) {
                clickButton();
              } else {
                iframe.onload = clickButton;
              }
            }
          }}
        >
          <FormattedMessage
            {...messages[
              `serviceRequest.${
                offerWithdrawn
                  ? "offerWithdrawn"
                  : offerAsked
                  ? "offerAsked"
                  : "askAnOffer"
              }`
            ]}
          />
        </Button>
      ) : (
        <Button
          flex="1"
          size="lg"
          colorScheme="seaGreen"
          onClick={() => {
            if (isSubscribed) {
              props.onCandidateOfferModalOpen(props.conversationId);
            } else if (serviceOfferId) {
              window.location.href = `/contracts/${serviceOfferId}/subscription_fee/new?id=${serviceOfferId}`;
            }
          }}
        >
          <FormattedMessage {...messages["serviceRequest.acceptOffer"]} />
        </Button>
      )}

      <HStack spacing="8px">
        <Button
          flex="1"
          size="lg"
          p="15px"
          background="transparent"
          border="1px"
          borderColor={unreadMessagesCount > 0 ? "gray.600" : "gray.300"}
          position="relative"
          _hover={{
            background: "initial",
            color: "gray.800",
            borderColor: "gray.500",
          }}
          data-bs-toggle="offcanvas"
          data-bs-target={`#offcanvas-conversation-${props.conversationId}`}
          aria-controls="offcanvasRight"
          onClick={() =>
            props.removeUnreadIndicatorFromConversation(props.conversationId)
          }
        >
          <Icon as={Comment} display={{ base: "block", md: "none" }} />
          <Text m="0" display={{ base: "none", md: "block" }}>
            <FormattedMessage {...messages["serviceRequest.discuss"]} />
          </Text>
          {unreadMessagesCount > 0 && (
            <Box
              position="absolute"
              top="-10px"
              right="-10px"
              background="red.500"
              color="white"
              minW="24px"
              h="24px"
              px="6px"
              borderRadius="999px"
              fontSize="12px"
              fontWeight="bold"
              lineHeight="24px"
              textAlign="center"
              sx={UNREAD_BADGE_SX}
            >
              {unreadMessagesCount}
            </Box>
          )}
        </Button>
        <Button
          flex="1"
          size="lg"
          p="15px"
          background="transparent"
          border="1px"
          borderColor="gray.300"
          position="relative"
          _hover={{
            background: "initial",
            color: "gray.800",
            borderColor: "gray.500",
          }}
          onClick={() => {
            onDeclineOfferModalOpen(props.conversationId);
          }}
        >
          <Icon as={Xmark} display={{ base: "block", md: "none" }} />
          <Text m="0" display={{ base: "none", md: "block" }}>
            <FormattedMessage {...messages["serviceRequest.decline"]} />
          </Text>
        </Button>
      </HStack>
    </HStack>
  );
};
